import type { EditableWithOverwrites } from "shared-types";

type ValidationType = "string" | "url" | "array";

const validatedFields: Record<string, { type: ValidationType; maxLength?: number }> = {
    tiktokAdName: { type: "string", maxLength: 512 },
    tiktokAdText: { type: "string", maxLength: 100 },
    tiktokCallToAction: { type: "array" },
    tiktokLandingPageUrl: { type: "url" }
};

function isValidHttpsUrl(str: string): boolean {
    try {
        const url = new URL(str.trim());
        const hasValidProtocol = url.protocol === "https:";
        const hasValidTld = /\.[a-z]{2,}$/i.test(url.hostname);

        return hasValidProtocol && hasValidTld;
    } catch {
        return false;
    }
}

export function getInvalidTikTokEditables(
    data: EditableWithOverwrites[],
    editableGroup: { label: string; value: string }
): { label: string; errors: { editableGroup: string; error: string }[] }[] {
    const errorsMap = new Map<string, string>();

    data.filter(
        ({ editable }) =>
            editable.publisherPlatform === "tiktok" && Object.keys(validatedFields).includes(editable.name)
    ).forEach(({ editable, overwrites }) => {
        const { name } = editable;
        const fieldConfig = validatedFields[name];
        if (!fieldConfig) {
            return;
        }
        const validationType = fieldConfig.type;

        const relevantOverwrites = overwrites.filter(
            o =>
                o.language === "en" &&
                Array.isArray(o.editableGroupValueIds) &&
                o.editableGroupValueIds.includes(editableGroup.value)
        );

        if (errorsMap.has(name)) {
            return;
        }

        if (relevantOverwrites.length === 0) {
            errorsMap.set(name, "has no value");
            return;
        }

        if (validationType === "string") {
            const value = relevantOverwrites.find(o => typeof o.value === "string")?.value;
            if (typeof value !== "string" || value.trim() === "") {
                errorsMap.set(name, "has no value");
            } else if (fieldConfig.maxLength && value.length > fieldConfig.maxLength) {
                errorsMap.set(name, `must be ${fieldConfig.maxLength} characters or less`);
            }
        }

        if (validationType === "url") {
            const urlValue = relevantOverwrites.find(o => typeof o.value === "string")?.value;

            if (typeof urlValue !== "string" || urlValue.trim() === "" || !isValidHttpsUrl(urlValue)) {
                errorsMap.set(name, "is not a valid HTTPS link");
            }
        }

        if (validationType === "array") {
            const hasSelected = relevantOverwrites.some(
                o => Array.isArray(o.value) && o.value.some(v => v.selected === true)
            );
            if (!hasSelected) {
                errorsMap.set(name, "has no value selected");
            }
        }
    });

    if (errorsMap.size === 0) {
        return [];
    }

    return [
        {
            label: editableGroup.label,
            errors: Array.from(errorsMap.entries()).map(([editableGroup, error]) => ({
                editableGroup,
                error
            }))
        }
    ];
}
